import {
  Component,
  Input,
  AfterViewInit,
  OnChanges,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  inject,
  input,
} from '@angular/core';
import { ChartData } from '../../interfaces/global/chart';
import { colors } from '../../constants/tableColors';
import { CommonModule } from '@angular/common';
import {
  Chart,
  PieController,
  ArcElement,
  ChartData as ChartJSData,
} from 'chart.js';
import { ChartComponent } from '../chart/chart.component';
import { DictionaryService } from '../../services/dictionary.service';

@Component({
  selector: 'app-pie',
  imports: [CommonModule],
  templateUrl: './pie.component.html',
  styleUrl: './pie.component.scss',
})
export class PieComponent
  extends ChartComponent
  implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
  @Input() data: ChartData = {} as ChartData;
  @Input() compactLegend = false;

  @ViewChild('piechartcanvas', { static: true }) public chartElm?: ElementRef;
  disableAnimations = input(false); // Disable animations for better performance with dynamic data

  private dictionaryService = inject(DictionaryService);
  private isRtl = this.dictionaryService.language === 'ar';

  constructor() {
    super();
    this.chartType = 'doughnut';
    Chart.register(PieController, ArcElement);
  }

  ngAfterViewInit(): void {
    if (this.chartElm) {
      this.chart = new Chart(this.chartElm.nativeElement, {
        type: 'doughnut',
        data: {
          labels: [],
          datasets: [
            {
              data: [],
              backgroundColor: colors,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: 4 / 3,
          animation: this.disableAnimations()
            ? false
            : {
                animateRotate: true,
                animateScale: false,
              },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
            datalabels: { display: false },
          },
        },
      });

      this.applyChartState();
    }
  }

  override ngOnChanges(): void {
    this.applyChartState();
  }

  private applyChartState(): void {
    if (!this.chart) return;

    if (this.isLoading) {
      this.setLoadingOptions();
    } else if (this.isDataAvailable()) {
      this.setDataOptions();
    } else {
      this.setEmptyOptions();
    }

    this.chart.update();
  }

  override setLoadingOptions(): void {
    if (!this.chart) return;

    this.chart.data = {
      datasets: [
        {
          data: [1],
          backgroundColor: [this.colorMap['--casper']],
          borderRadius: 5,
          offset: 8,
        },
      ],
    };

    this.chart.options = {
      ...this.chart.options,
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };
  }

  override setDataOptions(): void {
    if (!this.chart) return;

    this.chart.data = {
      labels: this.data.itemLabels,
      datasets: [
        {
          data: this.data.itemValues,
          backgroundColor: colors,
          borderRadius: 5,
          offset: 8,
        },
      ],
    };

    this.chart.options = {
      ...this.chart.options,
      plugins: {
        legend: {
          title: {
            display: true,
            text: this.data.chartLabel,
            color: this.colorMap['--text'],
            font: {
              weight: 'bold',
              size: this.fontSize,
            },
          },
          labels: {
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              const displayValues = this.data.itemDisplayValues;
              return datasets[0].data.map((data, i) => ({
                text: this.compactLegend
                  ? `${(chart.data.labels || [])[i]}`
                  : `${(chart.data.labels || [])[i]}: ${displayValues?.[i] ?? data}`,
                // @ts-ignore
                fillStyle: datasets[0].backgroundColor[i],
                index: i,
                strokeStyle: 'transparent',
                borderRadius: 2,
              }));
            },
            color: this.colorMap['--boulder'],
            font: {
              size: Math.max(this.fontSize / 1.5, 12),
            },
          },
          rtl: this.isRtl,
          position: 'top',
        },
        datalabels: {
          anchor: 'center',
          align: 'center',
          clamp: true,
          formatter: (value: number, context: any) => {
            if (Number(value) === 0) return '';

            const data = context.chart.data.datasets[0].data;
            const totalValue: number = data.reduce(
              (total: number, v: number) => total + v,
              0,
            );
            const percentage = Math.round(Number(value / totalValue) * 100);

            // Hide label for slices smaller than 3%
            if (percentage < 3) return '';

            return percentage + '%';
          },
          color: (context: any) => {
            const bgColors = context.dataset.backgroundColor as string[];
            console.log(bgColors);
            console.log(context.dataIndex);

            const bg = bgColors[context.dataIndex] || '#2BB5CF';
            // Calculate relative luminance to determine contrast
            const hex = bg.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16) / 255;
            const g = parseInt(hex.substring(2, 4), 16) / 255;
            const b = parseInt(hex.substring(4, 6), 16) / 255;
            const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
            return luminance > 0.6 ? '#395160' : '#ffffff';
          },
          font: {
            weight: 'bold',
            size: Math.max(this.fontSize * 0.75, 11),
          },
        },

        tooltip: {
          callbacks: {
            label: (context: any) => {
              const displayValues = this.data.itemDisplayValues;
              return (
                displayValues?.[context.dataIndex] ??
                Math.round(Number(context.dataset.data[context.dataIndex])) + ''
              );
            },
          },
          boxPadding: 5,
        },
      },
    };
  }

  override setEmptyOptions(): void {
    if (!this.chart) return;
  }

  override isDataAvailable(): boolean {
    return this.data && this.data.itemValues.length > 0;
  }
}
